import { faroCoreApiClient } from "@api/faro-core-api-client";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import {
  BaseCompanyIdProps,
  BaseProjectIdProps,
} from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { useToast, blue } from "@flat-ui";
import { Link } from "@mui/material";
import {
  ProjectMemberEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SHOW_ON_HOVER_CLASS } from "@utils/ui-utils";
import { isMemberActive } from "@utils/user-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";

interface Props
  extends BaseCompanyIdProps,
    Partial<BaseProjectIdProps>,
    BaseMemberProps {
  /** Recognizes on which subject this component is being used */
  subjectType: MemberTableSubject;
}

/**
 * Resend invite for a single unregistered member of:
 * projects when project is available and component rendered under the team tab of project overview,
 * workspace when project is not available because component is rendered under member on the workspace level.
 * This component is hidden by default and it is shown on table hover using ${SHOW_ON_HOVER_CLASS}.
 */
export function ResendInvitation({
  companyId,
  projectId,
  member,
  subjectType,
}: Props): JSX.Element | null {
  const { handleErrorWithToast } = useErrorContext();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { canResendInvitationToCompany } = useHasUserValidRoleCompanyLevel();

  const isProjectSubject = subjectType === "project";
  const isWorkspaceOrGroupOrTeamSubject =
    subjectType === "workspace" ||
    subjectType === "group" ||
    subjectType === "team";

  async function onResendInvitationClick(): Promise<void> {
    trackEvent({
      name: ProjectMemberEvents.resendInvitation,
      props: {
        dataType: isProjectSubject
          ? TableType.projectMembers
          : TableType.companyMembers,
      },
    });

    try {
      // Resend invite to project
      if (projectId && isProjectSubject) {
        // No need to be an asyncThunk as we don't need to update the store
        await faroCoreApiClient.projects.resendProjectMembershipInvite({
          companyId,
          projectId,
          email: member.email,
        });
      }

      // Resend invite to workspace, group or team
      if (!projectId && isWorkspaceOrGroupOrTeamSubject) {
        // No need to be an asyncThunk as we don't need to update the store
        await faroCoreApiClient.company.resendCompanyMembershipInvite({
          companyId,
          userIdentity: member.email,
        });
      }

      openToast({
        title: "Invitation sent",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `resendInvite-${Date.now().toString()}`,
        title: "Could not send invitation",
        error,
      });
    }
  }

  if (isMemberActive(member)) {
    return null;
  }

  // Only users with company level permissions can resend invitations to the workspace, group or team.
  if (isWorkspaceOrGroupOrTeamSubject && !canResendInvitationToCompany) {
    return null;
  }

  return (
    <Link
      onClick={onResendInvitationClick}
      underline="always"
      className={SHOW_ON_HOVER_CLASS}
      sx={{
        marginLeft: "10px",
        color: blue[500],
        textDecorationColor: blue[500],
        cursor: "pointer",
        // This will be overridden by parent component using SHOW_ON_HOVER_CLASS
        visibility: "hidden",
      }}
    >
      Resend Invite
    </Link>
  );
}
